import React, { useMemo, useState } from 'react'
import Table from 'react-bootstrap/Table'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Dropdown from 'react-bootstrap/Dropdown'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEllipsisV } from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-toastify'
import Search from 'shared/forms/search'
import TableLoading from 'shared/tables/table-loading'
import TableError from 'shared/tables/table-error'
import TableFooter from 'shared/tables/table-footer'
import useMunicipalitySettings from 'api/hooks/use-municipality-settings'
import { errorMessage } from 'admin/shared/errors'
import {
  useAccountGroups,
  useDeleteAccountGroup
} from 'api/hooks/use-account-groups'
import { scopeLabel, groupDimensions, groupLabel } from './scopes'

const COLUMN_COUNT = 6
const DEFAULT_PER_PAGE = 25
const ALL_DIMENSIONS = ''
// Seeded descriptions run to ~200 characters, which is the right length for the
// report assistant to read and far too long for a table cell. Truncate on a word
// boundary and keep the full text in the title attribute.
const DESCRIPTION_PREVIEW = 90

const descriptionPreview = (text) => {
  const value = (text || '').trim()
  if (value.length <= DESCRIPTION_PREVIEW) return value

  const clipped = value.slice(0, DESCRIPTION_PREVIEW)
  const lastSpace = clipped.lastIndexOf(' ')

  return `${(lastSpace > 40 ? clipped.slice(0, lastSpace) : clipped).replace(/[,.;:]$/, '')}…`
}

// One grouping's groups. `groupingId` is required in practice, but while the groupings are
// still loading the table renders its empty state rather than every grouping's groups.
//
// `toolbarStart` is the page's grouping select, rendered ahead of the search box so the three
// controls that decide which rows appear sit in one row.
const AccountGroupTable = ({ groupingId, toolbarStart, onView, onEdit }) => {
  const [params, setParams] = useState({
    page: 1,
    perPage: DEFAULT_PER_PAGE,
    search: ''
  })
  const [dimension, setDimension] = useState(ALL_DIMENSIONS)
  const {
    data: groups,
    isLoading,
    isError
  } = useAccountGroups({ groupingId, options: { enabled: Boolean(groupingId) } })
  const { data: settings } = useMunicipalitySettings({ options: {} })

  const dimensions = useMemo(() => groupDimensions(settings), [settings])

  // Filtered and searched client-side for the same reason the list is paged
  // client-side: the index endpoint is unpaginated on purpose (the edit modal
  // needs every group's member_ids to lock claimed codes), so react-query already
  // holds the whole set and a server round trip would buy nothing.
  //
  // Search covers code, name, and description because a seeded taxonomy puts the
  // useful words in the description — "Carl Perkins" and "Ad Valorem" appear
  // nowhere in a group named "300 Vocational Programs" — and because the code is
  // what someone reading a chart of accounts searches by.
  const filtered = useMemo(() => {
    const term = params.search?.trim().toLowerCase()

    return (groups || []).filter((group) => {
      const scope = group.scope_key || group.account_type
      if (dimension !== ALL_DIMENSIONS && scope !== dimension) return false
      if (!term) return true

      return `${groupLabel(group)} ${group.description || ''}`
        .toLowerCase()
        .includes(term)
    })
  }, [groups, dimension, params.search])

  const total = filtered.length
  const pages = Math.max(1, Math.ceil(total / params.perPage))
  // Deleting the last row on the final page — or narrowing the filter — drops the
  // list below the current page. Clamp here so the body never renders empty for a
  // frame before TableFooter's own range effect resets the page.
  const page = Math.min(params.page, pages)
  const visibleGroups = filtered.slice(
    (page - 1) * params.perPage,
    page * params.perPage
  )

  const { mutate: deleteGroup } = useDeleteAccountGroup({
    options: {
      onSuccess: () => toast.success('Account group deleted'),
      onError: (err) =>
        toast.error(errorMessage(err, 'Could not delete the account group'))
    }
  })

  const handleDelete = (group) => {
    if (
      window.confirm(
        `Delete “${groupLabel(group)}”? Its codes will report individually again.`
      )
    ) {
      deleteGroup(group.id)
    }
  }

  const handleDimensionChange = (value) => {
    setDimension(value)
    setParams((prev) => ({ ...prev, page: 1 }))
  }

  return (
    <>
      <Row className="mb-3 g-2">
        {toolbarStart && <Col md={3}>{toolbarStart}</Col>}
        <Col md={5}>
          <Search
            params={params}
            setParams={setParams}
            placeholder="Search code, name, or description"
          />
        </Col>
        <Col md={4}>
          <Form.Select
            aria-label="Filter by account segment"
            data-testid="dimension-filter"
            value={dimension}
            onChange={(e) => handleDimensionChange(e.target.value)}
          >
            <option value={ALL_DIMENSIONS}>All account segments</option>
            {dimensions.map((entry) => (
              <option key={entry.key} value={entry.key}>
                {entry.label}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      <div className="table-responsive">
        <Table hover>
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Account Segment</th>
              <th>Codes</th>
              <th>Description</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {isLoading && <TableLoading columns={COLUMN_COUNT} />}
            {isError && <TableError columns={COLUMN_COUNT} />}

            {!isLoading && !isError && !total && (
              <tr>
                <td
                  colSpan={COLUMN_COUNT}
                  className="text-center text-muted py-4"
                >
                  {groups?.length
                    ? 'No account groups match this search or account segment.'
                    : 'No account groups in this grouping yet. Create one to roll up codes in charts, pivots, and exports.'}
                </td>
              </tr>
            )}

            {!isLoading &&
              !isError &&
              visibleGroups.map((group) => (
                <tr key={group.id}>
                  <td>{group.code}</td>
                  <td>{group.name}</td>
                  <td>{scopeLabel(group, settings)}</td>
                  <td>{group.members_count}</td>
                  <td
                    className="text-muted small"
                    title={group.description || undefined}
                  >
                    {descriptionPreview(group.description)}
                  </td>
                  <td className="text-end">
                    <Dropdown align="end">
                      <Dropdown.Toggle
                        variant="light"
                        className="text-primary"
                        aria-label={`Actions for ${groupLabel(group)}`}
                      >
                        <FontAwesomeIcon icon={faEllipsisV} />
                      </Dropdown.Toggle>
                      {/* Fixed positioning lets the menu escape .table-responsive's
                          overflow clipping, so it draws over the pagination footer. */}
                      <Dropdown.Menu
                        renderOnMount
                        popperConfig={{ strategy: 'fixed' }}
                      >
                        <Dropdown.Item onClick={() => onView(group)}>
                          View
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => onEdit(group)}>
                          Edit
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item
                          className="text-danger"
                          onClick={() => handleDelete(group)}
                        >
                          Delete
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      </div>

      {!isLoading && !isError && total > 0 && (
        <TableFooter
          meta={{ total, pages, page }}
          params={{ page, perPage: params.perPage }}
          setParams={setParams}
        />
      )}
    </>
  )
}

export default AccountGroupTable
